import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  patientsApi,
  type AddHistoryEntryRequest,
  type CreatePatientRequest,
  type PatientResponse,
  type CheckPatientDuplicatesRequest,
  type PatientDuplicateMatchResponse,
  type MergePatientRequest,
  type NoteEntryInput,
  type SavePatientAlertRequest,
  type SaveClinicTagRequest,
  type UpdateHistoryEntryRequest,
  type UpdatePatientRequest,
} from '../infrastructure/patients.api';
import { patientKeys } from './patients.query';

/**
 * Commands — patient write use cases. Each hook invalidates the relevant read
 * caches on success so lists/detail refetch.
 */

/** Plain command — register a patient. Reusable outside React Query if needed. */
export function registerPatient(request: CreatePatientRequest): Promise<PatientResponse> {
  return patientsApi.register(request);
}

export function checkPatientDuplicates(request: CheckPatientDuplicatesRequest): Promise<PatientDuplicateMatchResponse[]> {
  return patientsApi.checkDuplicates(request);
}

export function useRegisterPatientCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreatePatientRequest) => registerPatient(request),
    // Awaited so a caller doing `await mutateAsync(...)` only proceeds once the
    // invalidated lists have actually refetched — otherwise a slow/failed background
    // refetch (e.g. against a cold-started server) silently leaves stale data on
    // screen with no error, and the only way to see the change is a hard refresh.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: patientKeys.all }),
  });
}

export function useArchivePatientCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patientId: string) => patientsApi.archive(patientId),
    onSuccess: (_data, patientId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: patientKeys.all }),
        queryClient.invalidateQueries({ queryKey: patientKeys.detail(patientId) }),
      ]),
  });
}

export function useRestorePatientCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patientId: string) => patientsApi.restore(patientId),
    onSuccess: (_data, patientId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: patientKeys.all }),
        queryClient.invalidateQueries({ queryKey: patientKeys.detail(patientId) }),
      ]),
  });
}

// Awaited (not `void`-fired) so `mutateAsync` callers only resolve once the
// refetch is done — a background refetch that silently errors (retry is
// disabled globally) would otherwise leave stale data on screen with no
// indication, which is what previously made changes seem to need a hard
// refresh to show up.
function usePatientMutation<TVariables, TData>(mutationFn: (variables: TVariables) => Promise<TData>, patientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: patientKeys.all }),
        queryClient.invalidateQueries({ queryKey: patientKeys.detail(patientId) }),
      ]),
  });
}

export function useUpdatePatientCommand(patientId: string) {
  return usePatientMutation((request: UpdatePatientRequest) => patientsApi.update(patientId, request), patientId);
}

export function useSetAllergySummaryCommand(patientId: string) {
  return usePatientMutation((allergySummary: string | null) => patientsApi.setAllergySummary(patientId, allergySummary), patientId);
}

export function useAddPatientAlertCommand(patientId: string) {
  return usePatientMutation((request: SavePatientAlertRequest) => patientsApi.addAlert(patientId, request), patientId);
}

export function useUpdatePatientAlertCommand(patientId: string) {
  return usePatientMutation(({ alertId, request }: { alertId: string; request: SavePatientAlertRequest }) => patientsApi.updateAlert(patientId, alertId, request), patientId);
}

export function useResolvePatientAlertCommand(patientId: string) {
  return usePatientMutation((alertId: string) => patientsApi.resolveAlert(patientId, alertId), patientId);
}

export function useReopenPatientAlertCommand(patientId: string) {
  return usePatientMutation((alertId: string) => patientsApi.reopenAlert(patientId, alertId), patientId);
}

export function useAddHistoryEntryCommand(patientId: string) {
  return usePatientMutation((request: AddHistoryEntryRequest) => patientsApi.addHistoryEntry(patientId, request), patientId);
}

export function useUpdateHistoryEntryCommand(patientId: string) {
  return usePatientMutation(
    ({ entryId, request }: { entryId: string; request: UpdateHistoryEntryRequest }) => patientsApi.updateHistoryEntry(patientId, entryId, request),
    patientId
  );
}

export function useResolveHistoryEntryCommand(patientId: string) {
  return usePatientMutation((entryId: string) => patientsApi.resolveHistoryEntry(patientId, entryId), patientId);
}

export function useReopenHistoryEntryCommand(patientId: string) {
  return usePatientMutation((entryId: string) => patientsApi.reopenHistoryEntry(patientId, entryId), patientId);
}

export function useReplaceFamilyHistoryCommand(patientId: string) {
  return usePatientMutation((entries: NoteEntryInput[]) => patientsApi.replaceFamilyHistory(patientId, entries), patientId);
}

export function useReplaceLifestyleSocialCommand(patientId: string) {
  return usePatientMutation((entries: NoteEntryInput[]) => patientsApi.replaceLifestyleSocial(patientId, entries), patientId);
}

export function useAssignPatientTagCommand(patientId: string) {
  return usePatientMutation((tagId: string) => patientsApi.assignTag(patientId, tagId), patientId);
}

export function useRemovePatientTagCommand(patientId: string) {
  return usePatientMutation((tagId: string) => patientsApi.removeTag(patientId, tagId), patientId);
}

export function useCreateClinicTagCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SaveClinicTagRequest) => patientsApi.createTag(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: patientKeys.all }),
  });
}

export function useUpdateClinicTagCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tagId, request }: { tagId: string; request: SaveClinicTagRequest }) =>
      patientsApi.updateTag(tagId, request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: patientKeys.all }),
  });
}

export function useMergePatientCommand(patientId: string) {
  return usePatientMutation((request: MergePatientRequest) => patientsApi.merge(patientId, request), patientId);
}

export function useReviewPatientDetailCorrectionCommand(patientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (correctionRequestId: string) =>
      patientsApi.reviewDetailCorrection(patientId, correctionRequestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: patientKeys.corrections(patientId) });
    },
  });
}
